import { LoadingOutlined } from "@ant-design/icons";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { type CoverageCardData, type CoverageRow, fetchCoverageCard } from "../coverage";
import {
  COVERAGE_BADGES,
  costLabel,
  feeOnlyLabel,
  rowDetail,
  selectedCostTotal,
  summaryCaption,
  summaryParts,
  summaryReason,
  totalLabel,
} from "./coverageModel";

// The coverage card: a SEPARATE widget that sits under an order card and
// says, per lab / imaging / procedure row, whether the patient's plan covers
// it, what the practice bills, and what the patient is likely to owe. The
// backend sends codes and figures; every sentence comes from coverageModel.
// A row whose reason is the card's own reason (the plan is unverified) shows
// only its badge; the sentence appears once at the top. It is fetched by
// order card id from the coverage endpoint; it never writes to the
// order card and the order card never reads it. Mounted only when the
// practice is flagged in (`coverage_card_enabled` on bootstrap).

interface CoverageCardProps {
  threadId: string;
  orderCardId?: string;
  clientId?: number;
  // The rows the clinician has checked on the order card above, if known.
  selected?: Set<number> | null;
  // The order card is history: show what the coverage was, no re-check.
  frozen?: boolean;
}

const money = (v: number): string => `$${Math.round(v).toLocaleString("en-US")}`;

const CoverageCard: React.FC<CoverageCardProps> = ({ threadId, orderCardId, clientId, selected, frozen }) => {
  const [data, setData] = useState<CoverageCardData | null | undefined>(undefined);
  const [busy, setBusy] = useState<"load" | "recheck" | null>("load");
  const alive = useRef(true);

  const load = useCallback(
    async (force: boolean) => {
      setBusy(force ? "recheck" : "load");
      const res = await fetchCoverageCard(threadId, orderCardId, { force, clientId });
      if (!alive.current) return;
      setData(res);
      setBusy(null);
    },
    [threadId, orderCardId, clientId],
  );

  useEffect(() => {
    alive.current = true;
    void load(false);
    return () => {
      alive.current = false;
    };
  }, [load]);

  if (data === null || (data && (!data.enabled || data.error))) return null;
  if (data === undefined) {
    return (
      <div style={styles.container} data-testid="coverage-card">
        <span style={styles.muted}>
          <LoadingOutlined /> Checking coverage…
        </span>
      </div>
    );
  }

  const rows: CoverageRow[] = (data.rows ?? []).filter((r) => !!r.coverage);
  const parts = summaryParts(data.summary);
  const caption = summaryCaption(data.summary);
  const total = selectedCostTotal(rows, selected);
  const summaryState = data.summary?.state;

  return (
    <div style={{ ...styles.container, ...(frozen ? styles.frozen : {}) }} data-testid="coverage-card">
      <div style={styles.header}>
        <span style={styles.title}>Coverage</span>
        {parts.length > 0 && (
          <span style={styles.strip} data-testid="coverage-strip">
            {parts.join(" · ")}
          </span>
        )}
      </div>
      {summaryState !== "verified" && summaryReason(data.summary) && (
        <div style={{ ...styles.muted, ...(summaryState === "inactive" ? styles.alert : {}) }}>
          {summaryReason(data.summary)}
        </div>
      )}
      {(data.summary?.plan_remarks ?? []).map((r) => (
        <div key={r} style={styles.muted}>
          {r}
        </div>
      ))}
      {rows.length > 0 && (
        <ul style={styles.rows}>
          {rows.map((r) => {
            const c = r.coverage;
            if (!c) return null;
            const badge = COVERAGE_BADGES[c.state] ?? COVERAGE_BADGES.unknown;
            const dimmed = !!selected && !selected.has(r.index);
            return (
              <li key={r.index} style={{ ...styles.row, ...(dimmed ? styles.dimmed : {}) }} data-testid="coverage-row">
                <span style={styles.rowMain}>
                  <span style={styles.rowName}>{r.test_name}</span>
                  {rowDetail(c, data.summary?.payer_name) && c.reason !== data.summary?.reason && (
                    <span style={styles.rowDetail} title={c.cpt ? `CPT ${c.cpt}` : undefined}>
                      {rowDetail(c, data.summary?.payer_name)}
                    </span>
                  )}
                  {(c.remarks ?? []).map((r) => (
                    <span key={r} style={styles.remark}>
                      {r}
                    </span>
                  ))}
                </span>
                <span style={styles.rowSide}>
                  <span
                    style={{
                      ...styles.badge,
                      color: badge.color,
                      backgroundColor: badge.bg,
                      borderColor: badge.border,
                    }}
                    title={rowDetail(c, data.summary?.payer_name)}
                  >
                    {badge.label}
                  </span>
                  {costLabel(c) ? (
                    <span style={styles.cost}>
                      <strong>{costLabel(c)}</strong>
                      {c.billed != null && <span style={styles.costDetail}>of {money(c.billed)} billed</span>}
                    </span>
                  ) : (
                    feeOnlyLabel(c) && (
                      <span style={styles.cost} data-testid="coverage-fee">
                        <span style={styles.feeOnly}>{feeOnlyLabel(c)?.billed}</span>
                        {feeOnlyLabel(c)?.allowed && <span style={styles.costDetail}>{feeOnlyLabel(c)?.allowed}</span>}
                      </span>
                    )
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <div style={styles.footer}>
        {total ? (
          <span data-testid="coverage-total">
            Patient pays <strong>{totalLabel(total)}</strong>
            {total.billed > 0 && <span style={styles.muted}> of {money(total.billed)} billed</span>}
          </span>
        ) : (
          <span />
        )}
        <span style={styles.footerRight}>
          {caption && <span style={styles.caption}>{caption}</span>}
          {!frozen && (
            <button
              type="button"
              style={styles.recheck}
              disabled={busy !== null}
              onClick={() => void load(true)}
              data-testid="coverage-recheck"
            >
              {busy === "recheck" ? "Re-checking…" : "Re-check with payer"}
            </button>
          )}
        </span>
      </div>
    </div>
  );
};

export default CoverageCard;

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginTop: "6px",
    padding: "10px 12px",
    border: "1px solid #E5E7EB",
    borderRadius: "10px",
    background: "#FAFAFA",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    fontSize: "12.5px",
    color: "#374151",
    // The thread's message container centres text; the card is a table.
    textAlign: "left",
  },
  frozen: { opacity: 0.7 },
  header: { display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap" },
  title: { fontSize: "11.5px", fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" },
  strip: { color: "#374151" },
  muted: { color: "#6B7280" },
  alert: { color: "#991B1B" },
  rows: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "6px" },
  row: { display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "flex-start" },
  dimmed: { opacity: 0.5 },
  rowMain: { display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0, gap: "2px", flex: 1 },
  rowName: { fontWeight: 500 },
  rowDetail: { fontSize: "11.5px", color: "#6B7280", lineHeight: 1.4 },
  remark: { fontSize: "11px", color: "#92400E", lineHeight: 1.4 },
  rowSide: { display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 },
  badge: { fontSize: "11px", fontWeight: 600, padding: "1px 7px", borderRadius: "999px", border: "1px solid" },
  cost: { display: "flex", flexDirection: "column", alignItems: "flex-end", lineHeight: 1.2 },
  costDetail: { fontSize: "10.5px", color: "#9CA3AF" },
  feeOnly: { color: "#6B7280" },
  footer: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" },
  footerRight: { display: "flex", alignItems: "center", gap: "10px", marginLeft: "auto" },
  caption: { fontSize: "11px", color: "#9CA3AF", textAlign: "right" },
  recheck: {
    fontSize: "11.5px",
    padding: "3px 9px",
    borderRadius: "6px",
    border: "1px solid #D1D5DB",
    background: "#FFFFFF",
    color: "#374151",
    cursor: "pointer",
  },
};
